import React from 'react';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';
import moment from 'moment';
import withChartExport from '../../../components/ChartExporting';
import { formatWorkDuration } from '../../../util/dates';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

import { getBreakdownData } from '../CTBActions';
import { saveConfig, updateBreakdown, updateBreakdownType } from '../../../components/Filter/FilterActions';

import barIcon from '../../../assets/img/bar-icon.svg';
import pieIcon from '../../../assets/img/pie-icon.svg';
import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import { escapeHTML } from '../../../util/text';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';
import legendIcon from '../../../assets/img/legend.svg';
import { ViewSwitch, CardList, PieLegend, lightSlice, switchSlice, toggledNames } from '../../../components/CardViews/CardViews';
import chartUtils from '../../../util/chartUtils';
import { WALLBOARD_TEXT_SIZE } from '../../../util/wallboardFrame';
const pieConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    spacing: [5, 5, 5, 5],
    events: {
      render: function() {
        chartUtils.drawSliceEdges(this);
      }
    }
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    width: 230,
    layout: 'vertical',        
    align: 'left',            
    verticalAlign: 'top',
    itemMarginTop: 4,    
    itemMarginBottom: 4,
    itemStyle: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: 'rgba(0, 0, 0, 0.87)',
    }
  },
  tooltip: {
    useHTML: true,
    outside: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'category'
  },
  plotOptions: {
    pie: {
      ...chartUtils.DONUT_OPTIONS,
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: false,
        useHTML: true,
        distance: 40,
        connectorWidth: 1,
        //alignTo: 'plotEdges',
        crop: false,
        overflow: 'allow',
        formatter: function () {},
        style: {
          fontSize: '14px',
          fontWeight: 'normal',
          textOutline: 'none'
        }
      }
    }
  },
  series: []
};

const barConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'bar'
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: true,
    useHTML: false,
    itemMarginTop: 2,
    itemMarginBottom: 2,
    itemStyle: {
      fontWeight: 'normal'
    }
  },
  tooltip: {
    useHTML: true,
    formatter: function() {}
  },
  yAxis: {
    title: {
      text: ''
    },
    labels: {
      format: '{value}%'
    },
    reversedStacks: false
  },
  plotOptions: {
    bar: {
      allowPointSelect: false,
      cursor: 'pointer',
      dataLabels: {
        enabled: true
      }
    },
    series: {
      stacking: 'percent'
    }
  },
  series: []
};

const DEFAULT_POS = { x: 95, y: 85 };

export class CTBreakdown extends React.Component {
  static defaultProps = {
    data: []
  };

  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();

    this.state = {
      isPieChart: true,
      isFullSreen: false,
      isLabelsVisible: !!props.wallmode,
      isListView: !props.wallmode,
      hoveredSlice: null,
      hiddenSlices: [],
      width: undefined,
      height: undefined,
      pos: DEFAULT_POS
    };
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    pieConfig.exporting = { ...pieConfig.exporting, ...this.props.chartExporting };
    barConfig.exporting = { ...barConfig.exporting, ...this.props.chartExporting };

    this.initChart();
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.updateFontSize();

    if (this.props.data.length) {
      this.props.getBreakdownData(this.props.data);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data != this.props.data) {
      this.props.getBreakdownData(this.props.data);
    }

    if (prevProps.breakdownData != this.props.breakdownData) {
      this.updateChartData();
      if (this.props.wallmode) {
        this.updateFontSize();
      }
    }

    if (prevProps.breakdownType != this.props.breakdownType) {
      this.updateChartType();
    }
  }

  exportValueText(point) {
    const time = minutes => formatWorkDuration(minutes, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays);
    if (this.props.timeUnit === 'cards') {
      const items = `${point.y} ${point.y === 1 ? 'item' : 'items'}`;
      return point.timeValue ? `${items} (${time(point.timeValue)})` : items;
    }
    const value = time(point.y);
    return point.countValue ? `${value} (${point.countValue} ${point.countValue === 1 ? 'item' : 'items'})` : value;
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChartType() {
    this.setState({ isPieChart: this.props.breakdownType === 'pie' });
    this.chart.destroy();

    this.initChart();
    this.updateFontSize();
    this.updateChartData();
  }

  initChart() {
    const config = this.props.breakdownType === 'pie' ? pieConfig : barConfig;
    const that = this;
    config.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };
        
    if (this.props.breakdownType === 'pie') {
      config.plotOptions.pie.point = {
        events: {
          mouseOver() {
            chartUtils.liftSlice(this, true);
            that.setState({ hoveredSlice: this.name });
          },
          mouseOut() {
            chartUtils.liftSlice(this, false);
            that.setState({ hoveredSlice: null });
          }
        }
      };
    }

    config.plotOptions[this.props.breakdownType].dataLabels.formatter =
      this.props.breakdownType === 'pie'
        ? function() {
            const name = escapeHTML(this.key || this.point.name);
            // Handle both count and time values based on timeUnit
            let value;
            if (that.props.timeUnit === 'cards') {
              value = `${this.y} ${this.y === 1 ? 'item' : 'items'}`;
              const timeVal = this.point.timeValue || this.timeValue;
              if (timeVal) {
                const timeStr = formatWorkDuration(timeVal, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                value += ` (${timeStr})`;
              }
            } else {
              value = formatWorkDuration(this.y, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
              const countVal = this.point.countValue || this.countValue;
              if (countVal) {
                value += ` (${countVal} ${countVal === 1 ? 'item' : 'items'})`;
              }
            }
            return `<b>${name}:</b><br/>${value}`;
          }
        : function() {
            return that.dataLabel(that, this);
          };

    this.chart = new Highcharts['Chart'](this.chartContainer.current, config);
  }

  updateChartData() {
    if (this.state.hiddenSlices.length) {
      this.setState({ hiddenSlices: [] });
    }
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (this.state.isPieChart) {
      // For pie chart, map the data to include custom properties in each point
      const pieData = chartUtils.donutData(this.props.breakdownData.map(item => ({
        name: item.name,
        y: item.y,
        color: item.color,
        countValue: item.countValue,
        timeValue: item.timeValue,
      })));

      this.chart.addSeries(
        {
          legendType: 'point',
          showInLegend: true,
          colorByPoint: true,
          data: pieData
        },
        true
      );
    } else {
      const barData = this.props.breakdownData;
      for (let i = 0; i < barData.length; i++) {
        const data = {
          ...barData[i],
          data: [{
            y: barData[i].y,
            countValue: barData[i].countValue,
            timeValue: barData[i].timeValue
          }]
        };
        this.chart.addSeries(data, false);
      }
      this.chart.redraw();
    }
  }

  updateFontSize = () => {
    if (!this.chart) return;

    const isFullscreen = this.state.isFullSreen;
    const isLabelsVisible = this.state.isLabelsVisible;
    const that = this;

    const exportPieLabels = this.state.isPieChart ? chartUtils.pieExportLabels(point => this.exportValueText(point)) : null;
    this.chart.update({
      exporting: {
        enabled: this.state.isFullSreen,
        chartOptions: {
          title: {
            text: 'Cycle Time Breakdown',
            style: {
              fontSize: '16px',
              fontWeight: 'bold'
            }
          },
          ...(exportPieLabels ? {} : {
            legend: {
              itemStyle: {
                color: 'rgba(0, 0, 0, 0.87)',
                fontSize: '12px',
                fontFamily: 'Poppins'
              }
            }
          }),
          plotOptions: {
            ...(exportPieLabels ? { pie: { dataLabels: exportPieLabels } } : {}),
            bar: {
              dataLabels: {
                style: {
                  textOutline: 'none'
                }
              }
            }
          }
        }
      },
      plotOptions: {
        bar: {
          dataLabels: chartUtils.segmentLabelStyle(this.props.wallmode ? WALLBOARD_TEXT_SIZE : (isFullscreen ? '13px' : '12px'))
        },
        pie: {
          size: isFullscreen ? '60%' : '90%',
          dataLabels: isFullscreen ? {
            enabled: true,
            useHTML: true,
            connectorWidth: 2,
            //alignTo: 'plotEdges',
            distance: 20,
            crop: false,
            overflow: 'justify',
            style: {
              fontSize: '16px',
              fontWeight: 'normal',
              textOutline: 'none',
              whiteSpace: 'normal',
            },
            formatter: function () {
              const name = escapeHTML(this.key || this.point.name);
              const percentage = this.percentage.toFixed(2);
              // Handle both count and time values based on timeUnit
              let value;
              if (that.props.timeUnit === 'cards') {
                value = `${this.y} ${this.y === 1 ? 'item' : 'items'}`;
                const timeVal = this.point.timeValue || this.timeValue;
                if (timeVal) {
                  const timeStr = formatWorkDuration(timeVal, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                  value += ` (${timeStr})`;
                }
              } else {
                value = formatWorkDuration(this.y, that.props.excludeWeekendOpts, that.props.timePrecision, that.props.useWorkingHoursInCycleTimeDays);
                const countVal = this.point.countValue || this.countValue;
                if (countVal) {
                  value += ` (${countVal} ${countVal === 1 ? 'item' : 'items'})`;
                }
              }
              return `<div class="tooltip-label-item tooltip-label-box" style="${chartUtils.pieLabelStyle(this, 20)}"><strong>${name} (${percentage}%)</strong>${value}</div>`;
            }
          } : {
            enabled: false,
            style: {
              fontSize: '11px'
            }
          }
        },
      },
      chart: {
        spacing: isFullscreen || !this.state.isPieChart ? [10, 10, 15, 10] : [5, 5, 5, 5]
      },
      legend: {
        enabled: isLabelsVisible,
        itemMarginTop: 4,
        ...(this.state.isPieChart || isFullscreen ? chartUtils.pieLegend(isFullscreen) : chartUtils.cardLegend(false))
      }
    });
    if (this.state.isPieChart) {
      this.chart.update({
        legend: { enabled: isFullscreen ? isLabelsVisible : false },
        plotOptions: { pie: { center: isFullscreen ? ['50%', '50%'] : chartUtils.cardPieCenter(this.chart, this.props.wallmode) } }
      });
    }
    chartUtils.placePieNames(this, this.state.isPieChart && !this.state.isFullSreen);
    this.chart.reflow();
  };

  handleSwitch = () => {
    this.setState({ isPieChart: !this.state.isPieChart }, () => {
      this.props.setChartType(this.state.isPieChart ? 'pie' : 'bar');
      this.props.saveConfig();
    });
  };
  
  handleClose = () => {
    this.props.close();
    this.props.saveConfig();
  };

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullSreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          isFullSreen: false,
          isLabelsVisible: this.labelsBeforeFocus,
          pos: DEFAULT_POS
        },
        () => this.updateChartType()
      );
    } else {
      this.labelsBeforeFocus = this.state.isLabelsVisible;
      this.setState(
        {
          width: '100%',
          height: '100%',
          isFullSreen: true,
          isLabelsVisible: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateChartType()
      );
    }
  };

  getView() {
    if (this.state.isListView) {
      return 'list';
    }
    return this.state.isPieChart ? 'pie' : 'bar';
  }

  handleView = view => {
    if (view === 'list') {
      this.setState({ isListView: true });
      return;
    }
    this.setState({ isListView: false });
    if ((view === 'pie') !== this.state.isPieChart) {
      this.handleSwitch();
    }
  };

  highlightSlice = (name, on) => {
    if (lightSlice(this.chart, name, on) || !on) {
      this.setState({ hoveredSlice: on ? name : null });
    }
  };

  toggleSlice = name => {
    switchSlice(this.chart, name);
    this.setState({ hiddenSlices: toggledNames(this.state.hiddenSlices, name) });
  };

  renderPieLegend() {
    return (
      <PieLegend
        offset={this.state.pieNamesOffset}
        data={this.props.breakdownData || []}
        hovered={this.state.hoveredSlice}
        hidden={this.state.hiddenSlices}
        onHover={this.highlightSlice}
        onToggle={this.toggleSlice}
      />
    );
  }

  renderRankedList() {
    const data = this.props.breakdownData || [];
    const isCards = this.props.timeUnit === 'cards';
    const rows = chartUtils.rankedShares(data).map(item => {
      const value = isCards
        ? `${item.y} ${item.y === 1 ? 'item' : 'items'}`
        : formatWorkDuration(item.y, this.props.excludeWeekendOpts, this.props.timePrecision, this.props.useWorkingHoursInCycleTimeDays);
      return { name: item.name, color: item.color, values: [item.share, value] };
    });
    return <CardList keyDots columns={['Share', isCards ? 'Items' : 'Cycle time']} rows={rows} />;
  }

  tooltipFormatter(comp, ev) {
    const name = escapeHTML(comp.props.breakdownType === 'pie' ? ev.key : ev.series.name);
    const point = ev.point || ev;
    const count = point.countValue !== undefined ? point.countValue : ev.y;
    const countValue = `${count} ${count === 1 ? 'work item' : 'work items'}`;
    const time = point.timeValue !== undefined ? point.timeValue : ev.y;
    const timeValue = formatWorkDuration(time, comp.props.excludeWeekendOpts, comp.props.timePrecision, comp.props.useWorkingHoursInCycleTimeDays);    
    
    return `
      <table style="margin: 0; padding: 0; border-spacing: 0;">
        <tr>
          <td colspan="2" style="padding: 0 0 4px 0;">
            <span style="background-color: ${ev.color}; display: inline-block; padding: 0px; width: 8px; height: 8px; border-radius: 50%; margin-right: 5px;"></span>
            <strong>${name}</strong>
          </td>
        </tr>
        <tr><td style="padding: 0; width: 40px;">Items:</td><td style="padding: 0 0 0 2px;">${countValue}</td></tr>
        <tr><td style="padding: 0; width: 40px;">Time:</td><td style="padding: 0 0 0 2px;">${timeValue}</td></tr>
        <tr><td style="padding: 0; width: 40px;">Share:</td><td style="padding: 0 0 0 2px;">${ev.percentage.toFixed(2)}%</td></tr>
      </table>
    `;
  }

  dataLabel(comp, ev) {
    return chartUtils.segmentLabel(ev);
  }

  render() {
    return (
      <Draggable position={this.state.pos} onStop={this.handleDrag} axis="both" bounds="parent">
        <div
          className={`box_white_shadow chart_averages_box breakdown ${this.state.isFullSreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className="box_white_title">
            <h3>Cycle Time Breakdown</h3>
            <div className='right-actions'>
              {/* the card's three views in one control: the ranked list, the pie and the bars; the active one is light blue */}
              <ViewSwitch
                views={[{ id: 'list', icon: legendIcon }, { id: 'pie', icon: pieIcon }, { id: 'bar', icon: barIcon }]}
                active={this.getView()}
                onChange={this.handleView}
              />
              <Button variant='secondary' size='sm' className={`btn-action ${this.state.isFullSreen ? 'fullscreen' : ''}`} onClick={this.handleResize}>
                <img src={this.state.isFullSreen ? expandIcon : collapseIcon} alt="toggleFullSreen" />
              </Button>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullSreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          
          <div id="breakdown_chart_container" ref={this.chartContainer} className={'no-stroke'} />
          {this.state.isListView && this.renderRankedList()}
          {!this.state.isListView && this.state.isPieChart && !this.state.isFullSreen && this.renderPieLegend()}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    getBreakdownData: data => dispatch(getBreakdownData(data)),
    close: () => dispatch(updateBreakdown(false, 'ctb')),
    setChartType: type => dispatch(updateBreakdownType(type, 'ctb')),
    saveConfig: () => dispatch(saveConfig())
  };
};

const mapStateToProps = state => {
  return {
    wallmode: state.app.wallmode,
    breakdownData: state.ctb.breakdown,
    timePrecision: state.filters.timePrecision,
    breakdownType: state.filters.ctb.breakdownType,
    timeUnit: state.filters.ctb.timeUnit,
    excludeWeekendOpts: {
      type: state.filters.excludeWeekends,
      workStart: state.filters.workStart,
      workEnd: state.filters.workEnd,
      holidays: state.filters.holidays
    },
    useWorkingHoursInCycleTimeDays: state.auth.user.config && state.auth.user.config.useWorkingHoursInCycleTimeDays
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(CTBreakdown));
